import axios from 'axios';
import i18n from 'helpers/i18n';
import messages from './messages';
import { clsx } from 'clsx';
import { useState } from 'react';
import { roleToText } from 'helpers/role';
import { getSubscriptionStatus } from 'actions/memberships';
import { Controller, useFormContext } from 'react-hook-form';
import { TextButton } from 'components/Organization/TextButton';
import { Select, SelectOption } from 'components/Organization/Select';
import EmailAutocomplete from 'components/Organization/EmailAutocomplete';
import RouteHelpers from 'helpers/routes';
import useIsPrimaryAdmin from '../hooks/useIsPrimaryAdmin';
import { useGetClientAccountRoles } from 'actions/roles';
import { Link } from 'react-router-dom';

interface UserFormProps {
  /** Current account id. */
  accountId: number;

  /** Subscription id. */
  subscriptionId: number;

  /** Index of the form. */
  index: number;

  /** List of roles. */
  roles: SelectOption[];

  /** Callback to remove the form. */
  remove?: () => void;

  /** Initial email value (optional, for first form only) */
  initialEmail?: string;
}

export function InviteUserForm({
  accountId,
  subscriptionId,
  index,
  roles,
  remove,
  initialEmail = ''
}: UserFormProps) {
  const {
    control,
    formState: { errors, touchedFields },
    setValue,
    setError,
    clearErrors,
    trigger
  } = useFormContext();

  const { data } = useGetClientAccountRoles(accountId);
  const isPrimaryAdmin = useIsPrimaryAdmin(data);

  const [subscriptionError, setSubscriptionError] = useState<
    string | React.ReactNode | null
  >(null);

  function checkSubscription(email: string, name?: string) {
    if (!email) {
      return;
    }

    getSubscriptionStatus({ email, subscription_id: subscriptionId })
      .then(response => {
        const {
          is_account_user,
          is_subscription_user,
          is_visible_to_current_user,
          subscription_role,
          subscription_status,
          user_name
        } = response.data;

        if (!is_account_user) {
          setSubscriptionError(i18n.ft(messages.errors.notAccountUser));
          setError(`members.${index}.email`, { type: 'manual' });
        } else if (is_subscription_user) {
          let message = (
            <>
              {i18n.ft(messages.errors[subscription_status], {
                name: user_name,
                role: roleToText(subscription_role)
              })}
            </>
          );

          if (!isPrimaryAdmin) {
            const adminHint = (
              <>
                {i18n.ft(messages.errors.adminHint1)}{' '}
                <Link
                  to={RouteHelpers.getPath('organization-add-members')}
                  state={{
                    email,
                    name,
                    subscriptionId: subscriptionId,
                    existingSubscriptionMember: true
                  }}
                >
                  {i18n.ft(messages.errors.adminHint2)}
                </Link>{' '}
                {i18n.ft(messages.errors.adminHint3)}
              </>
            );
            message = (
              <>
                {message} {adminHint}
              </>
            );
          }

          setSubscriptionError(message);
          setError(`members.${index}.email`, { type: 'manual' });
        } else if (!is_visible_to_current_user) {
          setSubscriptionError(
            <>
              {i18n.ft(messages.errors.notVisible1)}{' '}
              <Link
                to={RouteHelpers.getPath('organization-add-members')}
                state={{
                  email,
                  name,
                  subscriptionId: subscriptionId,
                  fromSubscriptionInvite: true
                }}
              >
                {i18n.ft(messages.errors.notVisible2)}
              </Link>{' '}
              {i18n.ft(messages.errors.notVisible3)}
            </>
          );
          setError(`members.${index}.email`, { type: 'manual' });
        } else {
          setSubscriptionError(null);
          clearErrors(`members.${index}.email`);
        }
      })
      .catch(error => {
        axios.isAxiosError(error) && error.response?.status === 404
          ? setSubscriptionError(null)
          : setSubscriptionError(i18n.ft(messages.errors.error));
      });
  }

  function setUserRole(role: string | null) {
    clearErrors(`members.${index}.role`);
    setValue(`members.${index}.role`, role);
  }

  function handleChange(value: string, field: any) {
    field.onChange(value);
    setSubscriptionError(null);
    clearErrors(`members.${index}.email`);
  }

  function handleFocus() {
    setSubscriptionError(null);
    clearErrors(`members.${index}.email`);
  }

  async function handleBlur(field: any) {
    if (field.value) {
      const isValid = await trigger(`members.${index}.email`);

      if (!isValid) {
        setError(`members.${index}.email`, {
          type: 'manual',
          message: i18n.ft(messages.errors.email)
        });
      } else {
        checkSubscription(field.value);
      }
    }
  }

  function handleSelect(customer: any) {
    setValue(`members.${index}.email`, customer.email);
    checkSubscription(customer.email, customer.name);
  }

  const handleClearForm = (index: number) => {
    setValue(`members.${index}.email`, '');
    setValue(`members.${index}.role`, '');
    setSubscriptionError(null);
  };

  function getFieldError(field: string) {
    // Prevent error when initialEmail is provided
    const isTouched =
      touchedFields?.members &&
      Array.isArray(touchedFields.members) &&
      touchedFields.members[index]?.[field];
    return isTouched && Array.isArray(errors.members) && errors.members[index]
      ? errors.members[index][field]?.message ?? ''
      : null;
  }

  const isRoleRequired = roles.length > 0;

  return (
    <section>
      {subscriptionError && (
        <div className="font-sans font-semibold text-base text-[#3C3F42] px-4 py-3.5 bg-red-100 border-x border-t border-error rounded-t-2xl">
          <i className="fa-solid fa-circle-xmark text-error mr-2" />
          <span>{subscriptionError}</span>
        </div>
      )}
      <div
        className={clsx(
          'bg-white p-6 pt-3 rounded-2xl',
          subscriptionError && 'rounded-t-none border-b border-x border-error'
        )}
      >
        <div className="flex justify-end">
          {remove ? (
            <TextButton size="small" type="button" onClick={remove}>
              {i18n.ft(messages.deleteForm)}
              <i className="fa-solid fa-trash-can ml-2" />
            </TextButton>
          ) : (
            <TextButton
              size="small"
              type="button"
              onClick={() => handleClearForm(0)}
            >
              {i18n.ft(messages.clearForm)}
              <i className="fa-solid fa-close ml-2" />
            </TextButton>
          )}
        </div>

        <div
          className={clsx(
            'grid gap-6',
            isRoleRequired ? 'grid-cols-2' : 'grid-cols-1'
          )}
        >
          <Controller
            name={`members.${index}.email`}
            control={control}
            render={({ field }) => (
              <div>
                <EmailAutocomplete
                  accountId={accountId}
                  value={field.value}
                  onChange={value => handleChange(value, field)}
                  onFocus={handleFocus}
                  onBlur={() => handleBlur(field)}
                  onSelect={handleSelect}
                  placeholder={i18n.ft(messages.emailPlaceholder)}
                />
                {getFieldError('email') && (
                  <p className="font-sans text-red-500 text-sm mt-1 font-semibold">
                    {getFieldError('email')}
                  </p>
                )}
              </div>
            )}
          />
          {isRoleRequired && (
            <Controller
              name={`members.${index}.role`}
              control={control}
              render={({ field }) => (
                <div>
                  <Select
                    value={field.value}
                    label={i18n.ft(messages.role)}
                    placeholder={i18n.ft(messages.rolePlaceholder)}
                    onChange={setUserRole}
                    options={roles}
                  />
                  {getFieldError('role') && (
                    <p className="font-sans text-red-500 text-sm mt-1 font-semibold">
                      {getFieldError('role')}
                    </p>
                  )}
                </div>
              )}
            />
          )}
        </div>
      </div>
    </section>
  );
}
